import React from "react";
import tw from "twin.macro";
import styled from "styled-components";

const Section = styled.section`
  ${tw`relative -mx-8 font-work_sans`}
  background: #fffbdc;
  padding: clamp(1.5rem, 1.2rem + 1.53vw, 3rem) clamp(1.5rem, 1rem + 2vw, 3.5rem);
`;

const Inner = tw.div`max-w-screen-xl mx-auto`;

const HeadingBlock = styled.div`
  ${tw`text-center flex flex-col items-center gap-4`}
  max-width: 48rem;
  margin: 0 auto clamp(3rem, 2.4rem + 2vw, 4.5rem);
`;

const Eyebrow = tw.span`uppercase tracking-[0.28em] text-[#14133B]/75 text-xs sm:text-sm`;
const Heading = tw.h2`text-3xl sm:text-4xl md:text-5xl font-semibold text-dark-500`;
const Subheading = tw.p`text-base sm:text-lg text-[#14133B]/75 max-w-3xl`;

const PostList = styled.ul`
  ${tw`flex flex-col`}
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: 1px solid rgba(20, 19, 59, 0.18);
`;

const PostItem = styled.li`
  ${tw`flex flex-col gap-4`}
  padding: clamp(1.4rem, 1rem + 1vw, 2rem) 0;
  border-bottom: 1px solid rgba(20, 19, 59, 0.18);
`;

const Meta = styled.span`
  ${tw`uppercase tracking-[0.24em] text-xs text-[#14133B]/75`}
`;

const PostTitle = styled.a`
  ${tw`text-2xl sm:text-3xl text-primary-500 uppercase font-roboto leading-tight`}
  text-decoration: none;
  transition: opacity 0.18s ease;

  &:hover {
    opacity: 0.75;
  }
`;

const ReadMore = styled.a`
  ${tw`inline-flex items-center justify-center font-semibold uppercase`}
  border-radius: 999px;
  padding: 0.75rem 1.8rem;
  letter-spacing: 0.14em;
  background: #14133b;
  color: #fffbdc;
  text-decoration: none;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
  align-self: flex-start;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 32px rgba(20, 19, 65, 0.18);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const FEATURED_POSTS = [
  {
    slug: "london-tv",
    title: "Grain Opens in Holborn: London’s First Seed-Oil-Free Mediterranean Kitchen",
    published: "London TV",
    href: "https://london-tv.co.uk/grain-opens-in-holborn-londons-first-seed-oil-free-mediterranean-kitchen-inspired-by-the-worlds-healthiest-blue-zones/",
    external: true,
  },
  {
    slug: "qsr-media",
    title: "Grain Brings Mediterranean Dining to Holborn With First Flagship Store",
    published: "QSR Media UK",
    href: "https://qsrmedia.co.uk/franchising/news/grain-brings-mediterranean-dining-holborn-first-flagship-store",
    external: true,
  },
  {
    slug: "travel-tour-world",
    title: "Grain Set to Revolutionize Fast-Casual Dining With Mediterranean Flavours",
    published: "Travel And Tour World",
    href: "https://www.travelandtourworld.com/news/article/grain-set-to-revolutionize-fast-casual-dining-with-mediterranean-flavors/",
    external: true,
  },
  {
    slug: "catering-today",
    title: "Mediterranean Restaurant Grain Opens First Site in Holborn",
    published: "Catering Today",
    href: "https://www.cateringtoday.co.uk/news/food/mediterranean-restaurant-grain-opens-first-site-in-holborn/",
    external: true,
  },
];

const POSTS = [
  {
    slug: "olive-oil",
    title: "Olive Oil: The Real Gold of the Mediterranean",
    excerpt:
      "Golden, fragrant, and alive, olive oil isn’t just an ingredient. It’s the philosophy that fuels the Mediterranean and the secret behind the world’s healthiest kitchens.",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/101761654029_.pic_hd.jpg",
    published: "October 2025",
  },
  {
    slug: "sourdough",
    title: "Sourdough: The Ancient Secret to a Healthier Gut",
    excerpt:
      "Long before industrial bread, there was sourdough—a living culture of wild yeast and time. Discover why this slow bread still wins on flavour, digestion, and gut health.",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/111761654030_.pic_hd.jpg",
    published: "October 2025",
  },
  {
    slug: "blue-zone",
    title: "The Blue Zone Secret: Why People Who Eat Like Mediterraneans Live to 100",
    excerpt:
      "From Sardinia to Ikaria, longevity isn’t a mystery—it’s a rhythm. Whole foods, olive oil, and community-powered meals reveal why simple eating leads to long, vibrant lives.",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/71761654027_.pic_hd.jpg",
    published: "October 2025",
  },
  {
    slug: "grain-rebellion",
    title: "The Grain Rebellion: How We’re Taking Carbs Back from the Diet Industry",
    excerpt:
      "Carbs were never the villain—confusion was. We’re reclaiming grains with tri-quinoa, lentils, and a Mediterranean mindset that favours nourishment over restriction.",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/81761654028_.pic_hd.jpg",
    published: "October 2025",
  },
  {
    slug: "mediterranean-way",
    title: "The Mediterranean Diet Isn’t a Diet; It’s a Way of Life",
    excerpt:
      "Forget quick fixes—this is a rhythm of real food, slow meals, and shared tables that blends balance, joy, and long-term wellbeing.",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/91761654028_.pic_hd.jpg",
    published: "October 2025",
  },
  {
    slug: "avocado-oil",
    title: "Why Avocado Oil Deserves a Place Beside Olive Oil",
    excerpt:
      "Olive oil has a timeless legacy, but avocado oil brings a smooth, modern balance that keeps the Mediterranean philosophy evolving.",
    image: "https://uk-grain.oss-eu-west-1.aliyuncs.com/grain-web/MENU/Breakfast_square/61761654027_.pic_hd.jpg",
    published: "October 2025",
  },
];

const BlogComponent = () => (
  <Section>
    <Inner>
      <HeadingBlock>
        <Eyebrow>Grain Journal</Eyebrow>
        <Heading>Stories From Our Kitchen</Heading>
        <Subheading>
          Food is more than fuel. Dive into the ingredients, rituals, and timeless wisdom that shape the Grain philosophy.
        </Subheading>
      </HeadingBlock>

      <PostList>
        {[...FEATURED_POSTS, ...POSTS].map(({ slug, published, title, href, external }) => {
          const link = external ? href : `/blog/${slug}`;
          const linkProps = external ? { target: "_blank", rel: "noopener noreferrer" } : {};

          return (
            <PostItem key={slug}>
              <Meta>{published}</Meta>
              <PostTitle href={link} {...linkProps}>
                {title}
              </PostTitle>
              <ReadMore href={link} {...linkProps}>
                Read Article
              </ReadMore>
            </PostItem>
          );
        })}
      </PostList>
    </Inner>
  </Section>
);

export default BlogComponent;
